/**
 * Custom error classes for the gaming-score pipeline (host-frontend).
 *
 * All errors are prefixed with [GameScore] for easy Sentry search/filtering.
 * Each carries a `machineId` for correlation and accepts an optional `context`
 * bag for attaching arbitrary diagnostic data via Sentry `extra`.
 */

const THROTTLE_MS = 60 * 60 * 1000; // 1 hour
const lastSentMap = new Map<string, number>();

/**
 * Returns true if this errorName:machineId pair was already reported within
 * the last hour. Call sites should skip Sentry.captureException when true.
 */
export function shouldThrottleGameScoreError(errorName: string, machineId: string): boolean {
  const key = `${errorName}:${machineId}`;
  const now = Date.now();
  const lastSent = lastSentMap.get(key);
  if (lastSent && now - lastSent < THROTTLE_MS) return true;
  lastSentMap.set(key, now);
  return false;
}

/** Clears the throttle map — use in test teardown. */
export function resetGameScoreThrottle(): void {
  lastSentMap.clear();
}

export class GamingScoreIpcNoReplyError extends Error {
  public readonly machineId: string;
  public readonly context?: Record<string, unknown>;

  constructor(machineId: string, context?: Record<string, unknown>) {
    super('[GameScore] Playjector never sent scoring metrics IPC reply');
    this.name = 'GamingScoreIpcNoReplyError';
    this.machineId = machineId;
    this.context = context;
  }
}

export class GamingScoreIpcValidationError extends Error {
  public readonly machineId: string;
  public readonly context?: Record<string, unknown>;

  constructor(machineId: string, context?: Record<string, unknown>) {
    super('[GameScore] IPC metrics reply failed strict validation');
    this.name = 'GamingScoreIpcValidationError';
    this.machineId = machineId;
    this.context = context;
  }
}

export class GamingScoreWsSubmitFailedError extends Error {
  public readonly machineId: string;
  public readonly context?: Record<string, unknown>;

  constructor(machineId: string, context?: Record<string, unknown>) {
    super('[GameScore] WebSocket disconnected — metrics queued, may be lost on restart');
    this.name = 'GamingScoreWsSubmitFailedError';
    this.machineId = machineId;
    this.context = context;
  }
}

export class GamingScoreTimeoutError extends Error {
  public readonly machineId: string;
  public readonly context?: Record<string, unknown>;

  constructor(machineId: string, context?: Record<string, unknown>) {
    super('[GameScore] 150s timeout — no gaming score arrived, assigning fallback Tier 1');
    this.name = 'GamingScoreTimeoutError';
    this.machineId = machineId;
    this.context = context;
  }
}

export class GamingScoreRestPollFailedError extends Error {
  public readonly machineId: string;
  public readonly context?: Record<string, unknown>;

  constructor(machineId: string, context?: Record<string, unknown>) {
    super('[GameScore] REST poll for authoritative score failed');
    this.name = 'GamingScoreRestPollFailedError';
    this.machineId = machineId;
    this.context = context;
  }
}
